/**
 * useRestaurants Hook
 * 
 * Custom hook for fetching and managing restaurant data.
 * Filters restaurants based on user permissions (role, group_ids, accessible_locations).
 * Supports multi-group access for group-admin users.
 */

import { useState, useEffect, useMemo } from 'react';
import { listRestaurants } from '../api/restaurantsApi';
import { useUser } from './useUser';

/**
 * Hook for fetching restaurants
 * 
 * @returns {Object} { restaurants, isLoading, error }
 */
const useRestaurants = () => {
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Get user permissions (supports multi-group)
  const { role, groupId, groupIds, accessibleLocations, canAccessLocation } = useUser();

  useEffect(() => {
    fetchRestaurants();
  }, []);

  /**
   * Fetch restaurants from API
   */
  const fetchRestaurants = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('🍔 Fetching restaurants...');
      const data = await listRestaurants();
      console.log('🍔 Restaurants received:', data);
      console.log('🍔 Number of restaurants:', data?.length || 0);
      
      if (data && Array.isArray(data)) {
        setAllRestaurants(data);
        console.log('✅ Set restaurants:', data.length);
      } else {
        console.warn('⚠️ Invalid restaurants data format:', data);
        setAllRestaurants([]);
      }
    } catch (err) {
      console.error('❌ Error in useRestaurants:', err);
      setError(err);
      setAllRestaurants([]);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Filter restaurants based on user permissions
   * Supports multi-group access (groupIds array)
   */
  const filterRestaurantsByPermissions = (restaurants) => {
    if (!restaurants || restaurants.length === 0) {
      console.log('🚫 No restaurants to filter');
      return [];
    }

    console.log('🔍 Filtering restaurants:', {
      total: restaurants.length,
      role,
      groupId,
      groupIds,
      accessibleLocations,
      accessibleLocationsType: typeof accessibleLocations,
    });

    // Super admin sees all restaurants
    if (role === 'super_admin') {
      console.log('🔓 Super admin - showing all restaurants');
      return restaurants;
    }

    // Group admin sees all restaurants in their groups (supports multi-group)
    if (role === 'group_admin' && groupIds && groupIds.length > 0) {
      const filtered = restaurants.filter(restaurant => 
        groupIds.includes(restaurant.groupId)
      );
      console.log(`🏢 Group admin for [${groupIds.join(', ')}] - showing ${filtered.length} restaurants`);
      return filtered;
    }

    // Location admin/staff see only their accessible locations
    if (Array.isArray(accessibleLocations) && accessibleLocations.length > 0) {
      const filtered = restaurants.filter(restaurant => 
        canAccessLocation(restaurant.locationId)
      );
      console.log(`📍 Location access - showing ${filtered.length} restaurants`);
      return filtered;
    }

    // If accessible_locations is "all_redbird" or similar string
    if (typeof accessibleLocations === 'string' && accessibleLocations.includes('all_') && groupId) {
      const filtered = restaurants.filter(restaurant => restaurant.groupId === groupId);
      console.log(`🏢 All group access for ${groupId} - showing ${filtered.length} restaurants`);
      return filtered;
    }

    // If groupIds exist but no specific role, show all restaurants in those groups
    if (groupIds && groupIds.length > 0) {
      const filtered = restaurants.filter(restaurant => 
        groupIds.includes(restaurant.groupId)
      );
      console.log(`🏢 Group IDs [${groupIds.join(', ')}] - showing ${filtered.length} restaurants`);
      if (filtered.length > 0) return filtered;
    }

    // Fallback to single groupId for backward compatibility
    if (groupId) {
      const filtered = restaurants.filter(restaurant => restaurant.groupId === groupId);
      console.log(`🏢 Group ID ${groupId} - showing ${filtered.length} restaurants`);
      if (filtered.length > 0) return filtered;
    }

    // If no permissions set, show all restaurants (for development/testing)
    // In production, you might want to return [] here
    console.log('⚠️ No specific permissions - showing all restaurants (development mode)');
    return restaurants;
  };

  // Apply filtering to restaurants - memoized to prevent infinite loops
  const restaurants = useMemo(() => 
    filterRestaurantsByPermissions(allRestaurants),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRestaurants, role, groupId, groupIds, accessibleLocations]
  );

  return {
    restaurants,
    isLoading,
    error,
    refetch: fetchRestaurants,
  };
};

export default useRestaurants;